import * as XLSX from "xlsx";
import { pb } from "./pocketbase";

/**
 * categoryImporterService.js — Serviço de Importação & Mapeamento de Categorias iSet / E-commerce
 *
 * Suporta o arquivo modelo `categorias-modelo.xlsx` (e XML/XLSX exportados da iSet):
 * - Mapeia: categories_id, categories_name, parent_id, categories_title, subtitle, page_url, hidden, sort_order, page_title, page_description
 * - Processamento IDEMPOTENTE por `categories_id` ou por `nome`: atualiza o registro existente ou cria novo se não existir.
 * - Constrói estrutura hierárquica (Categorias Pai -> Subcategorias)
 */
export const categoryImporterService = {
  /**
   * Faz o parse do arquivo Excel/XML contendo as categorias iSet
   */
  parseFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Converte para JSON com cabeçalho
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          
          if (!rows || rows.length === 0) {
            throw new Error("O arquivo selecionado está vazio ou sem dados válidos.");
          }

          const parsedCategories = rows.map((row, idx) => {
            const rawId = row.categories_id !== undefined && row.categories_id !== null ? String(row.categories_id).trim() : "";
            const categories_id = rawId && rawId !== "0" ? Number(rawId) || rawId : null;
            
            const rawParentId = row.parent_id !== undefined && row.parent_id !== null ? String(row.parent_id).trim() : "0";
            const parent_id = Number(rawParentId) || 0;

            const name = String(row.categories_name || row.nome || row.name || "").trim();
            const slug = String(row.page_url || row.slug || "").trim();
            const title = String(row.categories_title || row.title || "").trim();
            const subtitle = String(row.subtitle || "").trim();
            const hiddenVal = Number(row.hidden) || 0;

            return {
              _rowIndex: idx + 2,
              categories_id,
              nome: name,
              parent_id,
              slug,
              categories_title: title,
              subtitle,
              page_title: String(row.page_title || "").trim(),
              page_description: String(row.page_description || "").trim(),
              page_keywords: String(row.page_keywords || "").trim(),
              sort_order: Number(row.sort_order) || 0,
              ativo: hiddenVal === 0, // hidden = 0 significa visível/ativo
              detalhes: {
                subtitle,
                page_title: String(row.page_title || "").trim(),
                page_description: String(row.page_description || "").trim(),
                page_keywords: String(row.page_keywords || "").trim(),
                sort_order: Number(row.sort_order) || 0,
                last_modified: String(row.last_modified || "").trim()
              }
            };
          }).filter(c => c.nome.length > 0 || c.categories_id !== null);

          resolve({
            totalRows: rows.length,
            categories: parsedCategories
          });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  },

  /**
   * Executa a importação idempotente em PocketBase `produto_categorias`
   */
  async processImport({ categories, onProgress }) {
    const summary = {
      total: categories.length,
      processed: 0,
      created: 0,
      updated: 0,
      errors: []
    };

    // 1. Busca todos os registros existentes em produto_categorias
    let existingRecords = [];
    try {
      existingRecords = await pb.collection("produto_categorias").getFullList();
    } catch (e) {
      console.warn("Aviso ao carregar produto_categorias existentes:", e);
    }

    // Indexa por categories_id e por nome maiúsculo
    const mapById = {};
    const mapByName = {};

    existingRecords.forEach(rec => {
      if (rec.categories_id !== undefined && rec.categories_id !== null && String(rec.categories_id).trim() !== "") {
        mapById[String(rec.categories_id).trim()] = rec;
      }
      if (rec.nome) {
        mapByName[rec.nome.trim().toUpperCase()] = rec;
      }
    });

    for (let i = 0; i < categories.length; i++) {
      const cat = categories[i];
      summary.processed++;

      if (onProgress) {
        onProgress(Math.round(((i + 1) / categories.length) * 100));
      }

      if (!cat.nome && cat.categories_id === null) {
        continue;
      }

      const idKey = cat.categories_id !== null ? String(cat.categories_id).trim() : null;
      const nameKey = cat.nome ? cat.nome.trim().toUpperCase() : null;

      const existingRecord = (idKey && mapById[idKey]) || (nameKey && mapByName[nameKey]) || null;

      const payload = {
        nome: cat.nome || `Categoria ${cat.categories_id}`,
        categories_id: cat.categories_id,
        parent_id: cat.parent_id || 0,
        slug: cat.slug || "",
        categories_title: cat.categories_title || "",
        ativo: cat.ativo,
        detalhes: cat.detalhes || {}
      };

      try {
        if (existingRecord) {
          await pb.collection("produto_categorias").update(existingRecord.id, payload);
          summary.updated++;
        } else {
          const created = await pb.collection("produto_categorias").create(payload);
          summary.created++;
          // Atualiza mapas locais para manter consistência no mesmo lote
          if (idKey) mapById[idKey] = created;
          if (nameKey) mapByName[nameKey] = created;
        }
      } catch (err) {
        console.error(`Erro ao salvar categoria ${cat.nome} (ID: ${cat.categories_id}):`, err);
        summary.errors.push(`Cat ${cat.nome || cat.categories_id}: ${err.message}`);
      }
    }

    return summary;
  },

  /**
   * Constrói a estrutura de árvore hierárquica (Pai -> Subcategorias)
   * Tratamento robusto contra colições, nós órfãos e IDs de tipos mistos (PocketBase ID vs categories_id iSet)
   */
  buildTree(categoriesList) {
    if (!Array.isArray(categoriesList)) return [];

    const mapByPbId = {};
    const mapByCategoriesId = {};
    const roots = [];

    // 1. Primeira passagem: envelopa os nós com array children e indexa por ID PocketBase e por categories_id iSet
    const nodes = categoriesList.map(item => {
      const node = {
        ...item,
        children: []
      };
      if (item.id) {
        mapByPbId[String(item.id)] = node;
      }
      if (item.categories_id !== undefined && item.categories_id !== null && String(item.categories_id).trim() !== "" && String(item.categories_id) !== "0") {
        mapByCategoriesId[String(item.categories_id).trim()] = node;
      }
      return node;
    });

    // 2. Segunda passagem: vincula cada filho ao seu pai
    nodes.forEach(node => {
      const rawParentId = node.parent_id !== undefined && node.parent_id !== null ? String(node.parent_id).trim() : "0";

      // Se parent_id for 0 ou nulo, é nó raiz
      if (!rawParentId || rawParentId === "0" || rawParentId === "null" || rawParentId === "undefined") {
        roots.push(node);
      } else {
        // Tenta encontrar o nó pai pelo id do PocketBase primeiro, e depois pelo categories_id da iSet
        const parentNode = mapByPbId[rawParentId] || mapByCategoriesId[rawParentId];
        if (parentNode && parentNode !== node) {
          parentNode.children.push(node);
        } else {
          // Se o pai não for encontrado (ex: pai foi deletado/nó órfão), eleva a categoria a nó raiz para não sumir do painel
          roots.push(node);
        }
      }
    });

    return roots;
  },

  /**
   * Faz o parse do arquivo Excel de Relação Produto-Categoria (relacao-produtos-categorias.xlsx)
   * Espera colunas: products_id, categories_id, sort_order
   */
  parseRelationFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          if (!rows || rows.length === 0) {
            throw new Error("O arquivo de relações está vazio.");
          }

          const prodToCatsMap = {};
          let totalPairs = 0;

          rows.forEach(r => {
            const rawProdId = r.products_id !== undefined ? String(r.products_id).trim() : "";
            const rawCatId = r.categories_id !== undefined ? String(r.categories_id).trim() : "";

            if (rawProdId && rawCatId) {
              if (!prodToCatsMap[rawProdId]) prodToCatsMap[rawProdId] = [];
              if (!prodToCatsMap[rawProdId].includes(rawCatId)) {
                prodToCatsMap[rawProdId].push(rawCatId);
                totalPairs++;
              }
            }
          });

          resolve({
            totalRows: rows.length,
            uniqueProducts: Object.keys(prodToCatsMap).length,
            totalPairs,
            prodToCatsMap
          });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  },

  /**
   * Sincroniza o mapa de relações `prodToCatsMap` com o PocketBase (`catalog` e `produto_dados`)
   */
  async processRelationImport({ prodToCatsMap, onProgress }) {
    const summary = {
      totalProducts: Object.keys(prodToCatsMap).length,
      processed: 0,
      updatedCatalog: 0,
      updatedDados: 0,
      errors: []
    };

    try {
      // 1. Carrega todas as categorias para indexação por categories_id iSet
      const catList = await pb.collection("produto_categorias").getFullList();
      const catByIsetId = {};
      catList.forEach(c => {
        if (c.categories_id) {
          catByIsetId[String(c.categories_id).trim()] = c;
        }
      });

      // 2. Carrega todos os itens do catálogo
      const catalogItems = await pb.collection("catalog").getFullList();

      for (let i = 0; i < catalogItems.length; i++) {
        const item = catalogItems[i];
        const isetProdId = String(item?.full_data?.id || "").trim();

        if (onProgress) {
          onProgress({
            current: i + 1,
            total: catalogItems.length,
            sku: item.sku
          });
        }

        if (!isetProdId || !prodToCatsMap[isetProdId]) continue;

        const mappedCatIds = prodToCatsMap[isetProdId];
        const matchedCats = mappedCatIds
          .map(cid => catByIsetId[cid])
          .filter(Boolean);

        if (matchedCats.length === 0) continue;

        // Seleciona a categoria principal (prioriza nomes específicos sobre termos genéricos)
        let primaryCat = matchedCats[0];
        for (const mc of matchedCats) {
          const lower = mc.nome.toLowerCase();
          if (!["formatura", "feminino", "masculino", "anel", "prata 925", "ouro 18k"].includes(lower)) {
            primaryCat = mc;
            break;
          }
        }

        // Atualiza catalog
        const updatedFullData = {
          ...(item.full_data || {}),
          categories_ids: mappedCatIds,
          category_id: primaryCat.id,
          category: primaryCat.nome,
          categories: matchedCats.map(c => c.nome)
        };

        try {
          await pb.collection("catalog").update(item.id, {
            categories_id: primaryCat.categories_id || null,
            category: primaryCat.nome,
            full_data: updatedFullData
          });
          summary.updatedCatalog++;
        } catch (catErr) {
          console.warn(`Erro ao atualizar catálogo item ${item.sku}:`, catErr);
        }

        // Atualiza ou cria produto_dados para sku_pai
        const skuPai = item.sku.split("-")[0] + "-" + item.sku.split("-")[1];
        try {
          const dadosList = await pb.collection("produto_dados").getFullList({
            filter: `sku_pai = "${skuPai}"`
          });
          if (dadosList && dadosList.length > 0) {
            await pb.collection("produto_dados").update(dadosList[0].id, {
              categoria: primaryCat.id
            });
            summary.updatedDados++;
          } else {
            await pb.collection("produto_dados").create({
              sku_pai: skuPai,
              categoria: primaryCat.id,
              estado_modelagem: "sem_modelo"
            });
            summary.updatedDados++;
          }
        } catch (dadosErr) {
          console.warn(`Erro ao atualizar produto_dados SKU ${skuPai}:`, dadosErr);
        }

        summary.processed++;
      }
    } catch (err) {
      console.error("Erro geral na sincronização de relações:", err);
      summary.errors.push(err.message);
    }

    return summary;
  }
};
